// src/components/shift/ShiftHistoryTable.jsx
import React from 'react';
import { Clock, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const ShiftHistoryTable = ({ shifts }) => {
  const { language } = useApp();

  if (!shifts || shifts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>
          {language === 'en' ? 'No shift history' :
           language === 'km' ? 'គ្មានប្រវត្តិវេន' :
           '暂无班次历史'}
        </p>
      </div>
    );
  }

  const getDuration = (start, end) => {
    const diff = new Date(end) - new Date(start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}${language === 'en' ? 'h' : language === 'km' ? 'ម៉ោង' : '小时'} ${minutes}${language === 'en' ? 'm' : language === 'km' ? 'នាទី' : '分钟'}`;
  };

  const getDifference = (shift) => {
    const expected = parseFloat(shift.opening_cash || 0) + parseFloat(shift.total_sales || 0);
    const actual = parseFloat(shift.closing_cash || 0);
    return actual - expected;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Date' :
               language === 'km' ? 'កាលបរិច្ឆេទ' :
               '日期'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Cashier' :
               language === 'km' ? 'អ្នកគិតលុយ' :
               '收银员'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Duration' :
               language === 'km' ? 'រយៈពេល' :
               '时长'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Sales' :
               language === 'km' ? 'ការលក់' :
               '销售额'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Trans.' :
               language === 'km' ? 'ប្រតិបត្តិការ' :
               '交易数'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Cash' :
               language === 'km' ? 'ទឹកប្រាក់' :
               '现金'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Status' :
               language === 'km' ? 'ស្ថានភាព' :
               '状态'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {shifts.map((shift) => {
            const difference = getDifference(shift);
            const isBalanced = Math.abs(difference) < 0.01;
            
            return (
              <tr key={shift.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="text-gray-900">
                    {new Date(shift.start_time).toLocaleDateString(language === 'km' ? 'km-KH' : language)}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {new Date(shift.start_time).toLocaleTimeString(language === 'km' ? 'km-KH' : language, { hour: '2-digit', minute: '2-digit' })} - 
                    {new Date(shift.end_time).toLocaleTimeString(language === 'km' ? 'km-KH' : language, { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {shift.cashier_name || 'N/A'}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {getDuration(shift.start_time, shift.end_time)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center text-green-600 font-medium">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {parseFloat(shift.total_sales || 0).toFixed(2)}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {shift.transaction_count || 0}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="text-gray-600">
                    {language === 'en' ? 'Open' : language === 'km' ? 'បើក' : '期初'}: ${parseFloat(shift.opening_cash || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {language === 'en' ? 'Close' : language === 'km' ? 'បិទ' : '期末'}: ${parseFloat(shift.closing_cash || 0).toFixed(2)}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {isBalanced ? (
                    <div className="flex items-center text-green-600 text-sm">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      <span>
                        {language === 'en' ? 'Balanced' :
                         language === 'km' ? 'ស្មើ' :
                         '平衡'}
                      </span>
                    </div>
                  ) : (
                    <div className={`flex items-center text-sm ${
                      difference > 0 ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      <span>
                        {difference > 0 ? '+' : ''}{difference.toFixed(2)}
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ShiftHistoryTable;